// Prompt AD (2026-09-21) — "SITE PHOTOS" (Well Construction) y "PHOTOS"
// con etiqueta (Site Inspection: Well head, Pump label, Site area,
// Surroundings). El nombre de cada foto se guarda como description del
// archivo, mostrado debajo de la miniatura.
import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Camera, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SitePhotosCard({ entityType = 'job', entityId, category = 'site_photos', title = 'Site Photos', testId = 'site-photos-card' }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState('');
  const fileInputRef = useRef(null);

  const load = () => {
    api.get(`/files?entity_type=${entityType}&entity_id=${entityId}`)
      .then(r => setPhotos((r.data || []).filter(d => d.category === category)))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [entityId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', entityType);
      formData.append('entity_id', entityId);
      formData.append('category', category);
      if (label.trim()) formData.append('description', label.trim());
      await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Foto agregada');
      setLabel('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Card className="bg-white border-gray-100 shadow-sm" data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
          <Camera className="w-4 h-4 text-[#3399FF]" /> {title} {photos.length > 0 && <span className="text-xs font-normal text-gray-400">({photos.length})</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Well head, Pump label…" className="h-9 text-xs flex-1 min-w-[160px]" data-testid="site-photo-label-input" />
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleUpload} className="hidden" data-testid="site-photo-file-input" />
          <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="site-photo-add-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Add Photo
          </Button>
        </div>
        {loading ? (
          <p className="text-xs text-gray-400">Loading…</p>
        ) : photos.length === 0 ? (
          <p className="text-xs text-gray-400 italic" data-testid="site-photos-empty">No photos yet.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid="site-photos-grid">
            {photos.map(p => (
              <a key={p.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/download/${p.id}`} target="_blank" rel="noreferrer" className="group" data-testid={`site-photo-${p.id}`}>
                <div className="aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/download/${p.id}`} alt={p.description || ''} className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
                </div>
                {p.description && <p className="text-[10px] text-gray-500 mt-1 truncate text-center">{p.description}</p>}
              </a>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
